import { createSelector } from 'reselect';

import {
  getLocalisationVars,
  getOriginEntityId,
  getDestinationEntityId,
  getPreferDirects,
  getSelectedOutboundDate,
  getSelectedInboundDate,
  getStay,
} from '.';

import type { State } from './types';
import type {
  SkyscannerMichelinCommonVerticalV1Vertical,
  SkyscannerMichelinRecommendationsV1Recommendations,
  SkyscannerMichelinRecommendationsV1Recommendationtypes,
} from '@skyscanner-internal/michelin-web-http-client';

type RecommendationsRequest =
  SkyscannerMichelinRecommendationsV1Recommendations.RecommendationsRequest;

const formatDate = (date: Date) => ({
  localDate: {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  },
});

const getMichelinRecommendationsRequestSelector = createSelector(
  getLocalisationVars,
  getStay,
  getOriginEntityId,
  getDestinationEntityId,
  getSelectedOutboundDate,
  getSelectedInboundDate,
  getPreferDirects,
  (
    { currency, locale, market },
    { adults, childrenAges },
    originEntityId,
    destinationEntityId,
    outboundDate,
    inboundDate,
    preferDirects,
  ) => ({
    currency,
    locale,
    market,
    adults,
    childrenAges,
    originEntityId,
    destinationEntityId,
    outboundDate,
    inboundDate,
    preferDirects,
  }),
);

export const getMichelinRecommendationsRequest = (
  state: State,
  verticals: SkyscannerMichelinCommonVerticalV1Vertical.Vertical[],
  recommendationType: SkyscannerMichelinRecommendationsV1Recommendationtypes.RecommendationType,
): RecommendationsRequest => {
  const {
    adults,
    childrenAges,
    currency,
    destinationEntityId,
    inboundDate,
    locale,
    market,
    originEntityId,
    outboundDate,
    preferDirects,
  } = getMichelinRecommendationsRequestSelector(state);

  const travellers = [
    ...Array.from({ length: adults ?? 1 }, () => ({ exactAge: 30 })),
    ...childrenAges.map((age) => ({ exactAge: age })),
  ];

  return {
    requestContext: {
      culture: {
        market,
        locale,
        currencyCode: currency,
      },
    },
    searchContext: {
      verticals,
      recommendationType,
      outboundDate: formatDate(outboundDate),
      inboundDate: inboundDate ? formatDate(inboundDate) : undefined,
      originEntityId,
      destinationEntityId,
      travellers,
      searchFilters: { directFlight: preferDirects },
    },
  };
};

export const getMichelinRecommendation = (state: State) =>
  state.michelinRecommendations.recommendation;

export const getMichelinRecommendationsStatus = (state: State) =>
  state.michelinRecommendations.status;

export const getMichelinHotelContent = (state: State) =>
  state.michelinRecommendations.hotelContent;

export const getMichelinHotelContentStatus = (state: State) =>
  state.michelinRecommendations.hotelContentStatus;

// Extracts the hotel ID from raw recommendation state so it's available as
// soon as recommendations complete, before hotel content is fetched.
export const getMichelinHotelId = (state: State): string | undefined =>
  state.michelinRecommendations.recommendation?.verticalResults?.find(
    (v) => v.hotelResult,
  )?.hotelResult?.hotelId;

export const getRecommendationsError = (state: State): boolean =>
  state.michelinRecommendations.status === 'error';

// True while combo recommendation data is still being fetched.
// Also covers the brief gap between recommendation completing and hotel
// content starting (hotelContentStatus = 'idle' after recommendation completes).
export const isComboRecommendationLoading = (state: State): boolean => {
  const { hotelContentStatus, status } = state.michelinRecommendations;
  if (status === 'loading') return true;
  if (
    status === 'complete' &&
    (hotelContentStatus === 'idle' || hotelContentStatus === 'loading')
  )
    return true;
  return false;
};
